import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Add CSS for navigation scrollbar hiding
const navStyle = document.createElement('style');
navStyle.textContent = `
  .nav-scroll::-webkit-scrollbar {
    display: none;
  }
`;
if (!document.head.querySelector('style[data-nav-scroll]')) {
  navStyle.setAttribute('data-nav-scroll', 'true');
  document.head.appendChild(navStyle);
}

function TuitionTeacherDashboard({ user, onLogout }) {
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [activeSection, setActiveSection] = useState('students');

  useEffect(() => {
    fetchAssignedStudents();
  }, []);

  const fetchAssignedStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/tuition-students/${user.id}`);
      setAssignedStudents(response.data);
    } catch (error) {
      console.error('Error fetching assigned students:', error);
      setAssignedStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const impersonateStudent = (student) => {
    // Store current tuition teacher session
    const currentUser = JSON.parse(localStorage.getItem('user'));
    sessionStorage.setItem('originalTuitionTeacher', JSON.stringify(currentUser));
    sessionStorage.setItem('isTuitionTeacherImpersonating', 'true');
    
    // Create student session with impersonation flags
    const studentSession = {
      ...student,
      id: student._id,
      isImpersonated: true,
      impersonatedBy: user.name,
      impersonationType: 'tuition_teacher'
    };
    
    localStorage.setItem('user', JSON.stringify(studentSession));
    
    // Reload to switch to student dashboard
    window.location.reload();
  };

  const viewStudentDetails = (student) => {
    setSelectedStudent(student);
    setShowStudentModal(true);
  };

  const closeStudentModal = () => {
    setShowStudentModal(false);
    setSelectedStudent(null);
  };

  const getStudentsByClass = () => {
    const grouped = {};
    assignedStudents.forEach(student => {
      const className = student.class || 'Unassigned';
      if (!grouped[className]) {
        grouped[className] = [];
      }
      grouped[className].push(student);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="dashboard tuition-teacher-theme">
        <div className="header">
          <div className="profile-section">
            <div className="profile-pic">🎯</div>
            <div>
              <h3>{user.name}</h3>
              <p>Tuition Teacher</p>
            </div>
          </div>
          <button className="logout-btn" onClick={onLogout}>Logout</button>
        </div>
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <h3>Loading your assigned students...</h3>
        </div>
      </div>
    );
  }

  const studentsByClass = getStudentsByClass();

  const showSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="dashboard tuition-teacher-theme">
      <div className="header">
        <div className="profile-section">
          <div className="profile-pic">🎯</div>
          <div>
            <h3>{user.name}</h3>
            <p>Tuition Teacher</p>
          </div>
        </div>
        <button className="logout-btn" onClick={onLogout}>Logout</button>
      </div>

      {/* Compact Navigation Bar */}
      <div style={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 100, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        padding: '6px 0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}>
        <div style={{ 
          display: 'flex', 
          overflowX: 'auto', 
          gap: '4px', 
          padding: '0 15px',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} className="nav-scroll">
          {[
            { id: 'students', label: 'My Students' },
            { id: 'stats', label: 'Quick Stats' },
            { id: 'info', label: 'Teacher Info' }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => showSection(item.id)}
              style={{
                background: activeSection === item.id ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)',
                color: 'white',
                border: activeSection === item.id ? '2px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0.2)',
                padding: '6px 12px',
                borderRadius: '15px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: activeSection === item.id ? '700' : '600',
                minWidth: 'auto',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                transform: activeSection === item.id ? 'scale(1.05)' : 'scale(1)'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.25)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.15)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="dashboard-grid">
        <div id="students" className="card" style={{ gridColumn: 'span 2' }}>
          <h3>🎯 My Assigned Students</h3>
          
          {assignedStudents.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>📚</div>
              <h4>No Students Assigned Yet</h4>
              <p>Please contact the Admin Level 1 to assign students to your tuition classes.</p>
            </div>
          ) : (
            <>
              <div style={{ 
                marginBottom: '20px', 
                padding: '15px', 
                background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', 
                borderRadius: '10px', 
                border: '2px solid #4caf50' 
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>🎭 Tuition Teacher Powers</h4>
                <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '14px', color: '#2e7d32' }}>
                  <li>Access any assigned student's account directly (impersonation)</li>
                  <li>View students from different classes in one place</li>
                  <li>Full access to student dashboards, marksheets, and content</li>
                  <li>Cross-class student management capabilities</li>
                </ul>
              </div>

              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '20px',
                padding: '10px',
                background: '#f0f8ff',
                borderRadius: '8px'
              }}>
                <strong style={{ color: '#1565c0' }}>
                  Total Assigned Students: {assignedStudents.length}
                </strong>
                <div style={{ fontSize: '14px', color: '#1565c0' }}>
                  Classes: {Object.keys(studentsByClass).length}
                </div>
              </div>

              {Object.entries(studentsByClass).map(([className, students]) => (
                <div key={className} style={{ marginBottom: '25px' }}>
                  <h4 style={{ 
                    color: '#1565c0', 
                    borderBottom: '2px solid #2196f3', 
                    paddingBottom: '5px',
                    marginBottom: '15px'
                  }}>
                    📚 Class {className} ({students.length} students)
                  </h4>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                    gap: '15px' 
                  }}>
                    {students.map(student => (
                      <div key={student._id} style={{ 
                        padding: '15px', 
                        background: 'white', 
                        borderRadius: '10px', 
                        border: '2px solid #e0e0e0',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <div style={{ 
                            width: '50px', 
                            height: '50px', 
                            border: '2px solid #2196f3', 
                            background: student.profilePicture ? `url(${student.profilePicture})` : '#f0f0f0', 
                            backgroundSize: 'cover', 
                            backgroundPosition: 'center', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            fontSize: '20px', 
                            borderRadius: '50%'
                          }}>
                            {!student.profilePicture && '👤'}
                          </div>
                          <div>
                            <strong style={{ fontSize: '16px', color: '#333' }}>{student.name}</strong>
                            <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
                              Class: {student.class || 'Unassigned'}
                              {student.section && ` - Section ${student.section}`}
                            </p>
                            {student.studentId && (
                              <p style={{ margin: '2px 0', fontSize: '11px', color: '#888' }}>
                                ID: {student.studentId}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                          <p style={{ margin: '2px 0', wordBreak: 'break-word' }}>{student.email}</p>
                          {student.fathersName && (
                            <p style={{ margin: '2px 0' }}>Father: {student.fathersName}</p>
                          )}
                        </div>
                        
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button 
                            onClick={() => impersonateStudent(student)}
                            style={{ 
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                              color: 'white', 
                              border: 'none', 
                              padding: '8px 12px', 
                              borderRadius: '6px', 
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              flex: '1'
                            }}
                            title="Access student account directly"
                          >
                            🚪 Access Account
                          </button>
                          <button 
                            onClick={() => viewStudentDetails(student)}
                            style={{ 
                              background: '#2196f3', 
                              color: 'white', 
                              border: 'none', 
                              padding: '8px 12px', 
                              borderRadius: '6px', 
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}
                            title="View student details"
                          >
                            👁️ View
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div id="stats" className="card">
          <h3>📊 Quick Stats</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div style={{ padding: '15px', background: '#e3f2fd', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1565c0' }}>
                {assignedStudents.length}
              </div>
              <div style={{ fontSize: '12px', color: '#1565c0' }}>Total Students</div>
            </div>
            <div style={{ padding: '15px', background: '#f3e5f5', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6a1b9a' }}>
                {Object.keys(studentsByClass).length}
              </div>
              <div style={{ fontSize: '12px', color: '#6a1b9a' }}>Different Classes</div>
            </div>
          </div>
          
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ color: '#333', marginBottom: '10px' }}>Class Distribution:</h4>
            {Object.entries(studentsByClass).map(([className, students]) => (
              <div key={className} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '8px', 
                background: '#f8f9fa', 
                borderRadius: '4px', 
                marginBottom: '5px' 
              }}>
                <span style={{ fontWeight: 'bold' }}>Class {className}</span>
                <span style={{ color: '#666' }}>{students.length} students</span>
              </div>
            ))}
          </div>
        </div>

        <div id="info" className="card">
          <h3>ℹ️ Tuition Teacher Info</h3>
          <div style={{ padding: '15px', background: '#fff3e0', borderRadius: '8px', border: '1px solid #ff9800' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#e65100' }}>🎯 Your Role</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#e65100' }}>
              As a Tuition Teacher, you have special cross-class access to your assigned students.
            </p>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#e65100' }}>
              <li>Access students from multiple classes</li>
              <li>View all student data and progress</li>
              <li>Impersonate student accounts when needed</li>
              <li>Manage tuition-specific content</li>
            </ul>
          </div>
          
          <div style={{ marginTop: '15px', padding: '10px', background: '#e8f5e8', borderRadius: '6px' }}>
            <p style={{ margin: '0', fontSize: '12px', color: '#2e7d32', textAlign: 'center' }}>
              <strong>Need more students assigned?</strong><br/>
              Contact Admin Level 1 to modify your student assignments.
            </p>
          </div>
        </div>
      </div>

      {/* Student Details Modal */}
      {showStudentModal && selectedStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '15px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80%',
            overflowY: 'auto',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#333' }}>👤 Student Details</h3>
              <button 
                onClick={closeStudentModal}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                ✕ Close
              </button>
            </div>
            
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                border: '3px solid #2196f3', 
                background: selectedStudent.profilePicture ? `url(${selectedStudent.profilePicture})` : '#f0f0f0', 
                backgroundSize: 'cover', 
                backgroundPosition: 'center', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '32px', 
                borderRadius: '50%',
                margin: '0 auto 10px auto'
              }}>
                {!selectedStudent.profilePicture && '👤'}
              </div>
              <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>{selectedStudent.name}</h4>
              <p style={{ margin: 0, color: '#666' }}>{selectedStudent.email}</p>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div>
                <strong>Class:</strong>
                <p style={{ margin: '5px 0', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                  {selectedStudent.class || 'Unassigned'}
                  {selectedStudent.section && ` - Section ${selectedStudent.section}`}
                </p>
              </div>
              <div>
                <strong>Student ID:</strong>
                <p style={{ margin: '5px 0', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                  {selectedStudent.studentId || 'Not set'}
                </p>
              </div>
              <div>
                <strong>Father's Name:</strong>
                <p style={{ margin: '5px 0', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                  {selectedStudent.fathersName || 'Not set'}
                </p>
              </div>
              <div>
                <strong>Class Rank:</strong>
                <p style={{ margin: '5px 0', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                  {selectedStudent.classRank || 'Not set'}
                </p>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button 
                onClick={() => {
                  closeStudentModal();
                  impersonateStudent(selectedStudent);
                }}
                style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                  color: 'white', 
                  border: 'none', 
                  padding: '12px 20px', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                🚪 Access Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TuitionTeacherDashboard;